import type { SettingsMenuContainer } from '../settings-menu-container'

import { Events } from '|>/shared/events'
import { bind_ } from '|>/shared/fn'
import { register } from '|>/shared/vjs'
import { BaseComponent } from '../../base'
import { LocalEvents } from '../local-events'
import { SettingsSubtitlesBackgroundColorMenu } from './custom-menu/settings-subtitles-background-color-menu'
import { SettingsSubtitlesBackgroundOpacityMenu } from './custom-menu/settings-subtitles-background-opacity-menu'
import { SettingsSubtitlesCharacterEdgeStyleMenu } from './custom-menu/settings-subtitles-character-edge-style-menu'
import { SettingsSubtitlesCustomMenuMain } from './custom-menu/settings-subtitles-custom-menu-main'
import { SettingsSubtitlesFontColorMenu } from './custom-menu/settings-subtitles-font-color-menu'
import { SettingsSubtitlesFontFamilyMenu } from './custom-menu/settings-subtitles-font-family-menu'
import { SettingsSubtitlesFontOpacityMenu } from './custom-menu/settings-subtitles-font-opacity-menu'
import { SettingsSubtitlesFontSizeMenu } from './custom-menu/settings-subtitles-font-size-menu'
import { SettingsSubtitlesWindowColorMenu } from './custom-menu/settings-subtitles-window-color-menu'
import { SettingsSubtitlesWindowOpacityMenu } from './custom-menu/settings-subtitles-window-opacity-menu'
import { resetTextTrackCustomSettings } from './text-track-custom-settings'

const PREVIEW_SUBTITLE_LABEL = 'preview_subtitle_label'
const MAX_VOD_LENGTH = 999999

/**
 * Settings subtitles custom menu with internal navigation
 */
@register
export class SettingsSubtitlesCustomMenu extends BaseComponent {
  declare container: SettingsMenuContainer | undefined

  // main menu
  declare mainMenuEl: SettingsSubtitlesCustomMenuMain

  // sub-menus
  declare fontFamilyMenu: InstanceType<typeof SettingsSubtitlesFontFamilyMenu>
  declare fontColorMenu: InstanceType<typeof SettingsSubtitlesFontColorMenu>
  declare fontSizeMenu: InstanceType<typeof SettingsSubtitlesFontSizeMenu>
  declare fontOpacityMenu: InstanceType<typeof SettingsSubtitlesFontOpacityMenu>
  declare backgroundColorMenu: InstanceType<
    typeof SettingsSubtitlesBackgroundColorMenu
  >
  declare backgroundOpacityMenu: InstanceType<
    typeof SettingsSubtitlesBackgroundOpacityMenu
  >
  declare windowColorMenu: InstanceType<typeof SettingsSubtitlesWindowColorMenu>
  declare windowOpacityMenu: InstanceType<
    typeof SettingsSubtitlesWindowOpacityMenu
  >
  declare characterEdgeStyleMenu: InstanceType<
    typeof SettingsSubtitlesCharacterEdgeStyleMenu
  >

  private previewTrack: TextTrack | null = null

  constructor(player: any, options: any) {
    super(player, options)

    // Activate main menu by default (all others are deactivated)
    this.mainMenuEl.activate()

    // Bind navigation methods
    const navigateToFontFamily = bind_(this, this.navigateToFontFamily)
    const navigateToFontColor = bind_(this, this.navigateToFontColor)
    const navigateToFontSize = bind_(this, this.navigateToFontSize)
    const navigateToFontOpacity = bind_(this, this.navigateToFontOpacity)
    const navigateToBackgroundColor = bind_(
      this,
      this.navigateToBackgroundColor
    )
    const navigateToBackgroundOpacity = bind_(
      this,
      this.navigateToBackgroundOpacity
    )
    const navigateToWindowColor = bind_(this, this.navigateToWindowColor)
    const navigateToWindowOpacity = bind_(this, this.navigateToWindowOpacity)
    const navigateToCharacterEdgeStyle = bind_(
      this,
      this.navigateToCharacterEdgeStyle
    )
    const navigateToMain = bind_(this, this.navigateToMain)

    // Listen to internal navigation events
    this.on(
      LocalEvents.SettingsActivateSubtitlesFontFamilyMenu,
      navigateToFontFamily
    )
    this.on(
      LocalEvents.SettingsActivateSubtitlesFontColorMenu,
      navigateToFontColor
    )
    this.on(
      LocalEvents.SettingsActivateSubtitlesFontSizeMenu,
      navigateToFontSize
    )
    this.on(
      LocalEvents.SettingsActivateSubtitlesFontOpacityMenu,
      navigateToFontOpacity
    )
    this.on(
      LocalEvents.SettingsActivateSubtitlesBackgroundColorMenu,
      navigateToBackgroundColor
    )
    this.on(
      LocalEvents.SettingsActivateSubtitlesBackgroundOpacityMenu,
      navigateToBackgroundOpacity
    )
    this.on(
      LocalEvents.SettingsActivateSubtitlesWindowColorMenu,
      navigateToWindowColor
    )
    this.on(
      LocalEvents.SettingsActivateSubtitlesWindowOpacityMenu,
      navigateToWindowOpacity
    )
    this.on(
      LocalEvents.SettingsActivateSubtitlesCharacterEdgeStyleMenu,
      navigateToCharacterEdgeStyle
    )
    this.on(LocalEvents.SettingsActivateSubtitlesCustomMenu, navigateToMain)
  }

  update() {
    // Update main menu - pass SettingsSubtitlesCustomMenu as container
    // so items trigger events on this component, not on SettingsMenuContainer
    this.mainMenuEl.container = this
    this.mainMenuEl.update()
    this.mainMenuEl.setResetHandler(this.resetSettings.bind(this))

    // Update backTitle to use parent container for back navigation
    this.mainMenuEl.backTitle.container = this.container

    // Update all sub-menus - pass SettingsSubtitlesCustomMenu as container
    // so their backTitle triggers events on this component
    this.fontFamilyMenu.container = this
    this.fontFamilyMenu.update()

    this.fontColorMenu.container = this
    this.fontColorMenu.update()

    this.fontSizeMenu.container = this
    this.fontSizeMenu.update()

    this.fontOpacityMenu.container = this
    this.fontOpacityMenu.update()

    this.backgroundColorMenu.container = this
    this.backgroundColorMenu.update()

    this.backgroundOpacityMenu.container = this
    this.backgroundOpacityMenu.update()

    this.windowColorMenu.container = this
    this.windowColorMenu.update()

    this.windowOpacityMenu.container = this
    this.windowOpacityMenu.update()

    this.characterEdgeStyleMenu.container = this
    this.characterEdgeStyleMenu.update()
  }

  resetSettings() {
    // Reset settings to defaults
    const defaultSettings = resetTextTrackCustomSettings()

    // Trigger event for player to update classes
    this.player_.trigger(
      Events.TextTrack.CustomSettingsUpdated,
      defaultSettings
    )

    // Update main menu items to reflect the reset
    this.mainMenuEl.update()

    // Update all sub-menus to reflect the reset
    this.fontFamilyMenu.update()
    this.fontColorMenu.update()
    this.fontSizeMenu.update()
    this.fontOpacityMenu.update()
    this.backgroundColorMenu.update()
    this.backgroundOpacityMenu.update()
    this.windowColorMenu.update()
    this.windowOpacityMenu.update()
    this.characterEdgeStyleMenu.update()
  }

  activate() {
    this.addClass('vjs-x-active')

    // Show preview subtitle if all tracks are disabled
    if (this.areSubtitlesDisabled()) {
      this.showSubtitlePreview()
    }

    // Navigate to main menu and update height
    this.navigateToMain()
  }

  deactivate() {
    this.removeClass('vjs-x-active')
    this.container?.removeClass('vjs-x-custom-menu-active')
    this.deactivateAllSubMenus()

    // Hide preview subtitle
    this.hideSubtitlePreview()
  }

  // Navigation methods
  private deactivateAllSubMenus() {
    this.mainMenuEl.deactivate()
    this.fontFamilyMenu.deactivate()
    this.fontColorMenu.deactivate()
    this.fontSizeMenu.deactivate()
    this.fontOpacityMenu.deactivate()
    this.backgroundColorMenu.deactivate()
    this.backgroundOpacityMenu.deactivate()
    this.windowColorMenu.deactivate()
    this.windowOpacityMenu.deactivate()
    this.characterEdgeStyleMenu.deactivate()
  }

  private navigateToMain() {
    this.deactivateAllSubMenus()
    this.mainMenuEl.activate()
    // Add special class for wider menu
    if (this.container) {
      this.container.addClass('vjs-x-custom-menu-active')
    }
    this.updateContainerHeight(this.mainMenuEl.el_.scrollHeight)
  }

  private navigateToFontFamily() {
    this.deactivateAllSubMenus()
    this.fontFamilyMenu.activate() // activate() calls update() automatically
    // Remove special class for standard width
    if (this.container) {
      this.container.removeClass('vjs-x-custom-menu-active')
    }
    this.updateContainerHeight(this.fontFamilyMenu.el_.scrollHeight)
  }

  private navigateToFontColor() {
    this.deactivateAllSubMenus()
    this.fontColorMenu.activate()
    // Remove special class for standard width
    if (this.container) {
      this.container.removeClass('vjs-x-custom-menu-active')
    }
    this.updateContainerHeight(this.fontColorMenu.el_.scrollHeight)
  }

  private navigateToFontSize() {
    this.deactivateAllSubMenus()
    this.fontSizeMenu.activate()
    // Remove special class for standard width
    if (this.container) {
      this.container.removeClass('vjs-x-custom-menu-active')
    }
    this.updateContainerHeight(this.fontSizeMenu.el_.scrollHeight)
  }

  private navigateToFontOpacity() {
    this.deactivateAllSubMenus()
    this.fontOpacityMenu.activate()
    // Remove special class for standard width
    if (this.container) {
      this.container.removeClass('vjs-x-custom-menu-active')
    }
    this.updateContainerHeight(this.fontOpacityMenu.el_.scrollHeight)
  }

  private navigateToBackgroundColor() {
    this.deactivateAllSubMenus()
    this.backgroundColorMenu.activate()
    // Remove special class for standard width
    if (this.container) {
      this.container.removeClass('vjs-x-custom-menu-active')
    }
    this.updateContainerHeight(this.backgroundColorMenu.el_.scrollHeight)
  }

  private navigateToBackgroundOpacity() {
    this.deactivateAllSubMenus()
    this.backgroundOpacityMenu.activate()
    // Remove special class for standard width
    if (this.container) {
      this.container.removeClass('vjs-x-custom-menu-active')
    }
    this.updateContainerHeight(this.backgroundOpacityMenu.el_.scrollHeight)
  }

  private navigateToWindowColor() {
    this.deactivateAllSubMenus()
    this.windowColorMenu.activate()
    // Remove special class for standard width
    if (this.container) {
      this.container.removeClass('vjs-x-custom-menu-active')
    }
    this.updateContainerHeight(this.windowColorMenu.el_.scrollHeight)
  }

  private navigateToWindowOpacity() {
    this.deactivateAllSubMenus()
    this.windowOpacityMenu.activate()
    // Remove special class for standard width
    if (this.container) {
      this.container.removeClass('vjs-x-custom-menu-active')
    }
    this.updateContainerHeight(this.windowOpacityMenu.el_.scrollHeight)
  }

  private navigateToCharacterEdgeStyle() {
    this.deactivateAllSubMenus()
    this.characterEdgeStyleMenu.activate()
    // Remove special class for standard width
    if (this.container) {
      this.container.removeClass('vjs-x-custom-menu-active')
    }
    this.updateContainerHeight(this.characterEdgeStyleMenu.el_.scrollHeight)
  }

  private updateContainerHeight(height: number) {
    if (this.container?.el_) {
      // Set current height for smooth transition
      const currentHeight = this.container.el_.offsetHeight
      this.container.el_.style.height = `${currentHeight}px`

      // Update to new height on next frame for CSS transition
      this.container.requestAnimationFrame(() => {
        if (this.container?.el_) {
          this.container.el_.style.height = `${height}px`
        }
      })
    }
  }

  /**
   * Check if all subtitle tracks are disabled
   */
  private areSubtitlesDisabled(): boolean {
    const tracks = this.player_.textTracks()
    if (!tracks || tracks.length === 0) return true

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i]
      if (
        (track.kind === 'captions' || track.kind === 'subtitles') &&
        track.mode === 'showing' &&
        track.label !== PREVIEW_SUBTITLE_LABEL
      ) {
        return false
      }
    }
    return true
  }

  /**
   * Show preview subtitle with localized text
   */
  private showSubtitlePreview(): void {
    if (this.previewTrack) return // Already showing

    // Check if VTTCue is available
    if (typeof VTTCue === 'undefined') {
      return
    }

    const text = this.localize('subtitles_preview')
    const currentTime = this.player_.currentTime() || 0
    const duration = this.player_.duration()
    const isLive = duration === Infinity

    const previewTrack = this.player_.addTextTrack(
      'subtitles',
      PREVIEW_SUBTITLE_LABEL,
      'preview'
    )

    if (previewTrack) {
      const endTime = isLive
        ? Number.MAX_SAFE_INTEGER
        : duration || MAX_VOD_LENGTH

      const cue = new VTTCue(currentTime, endTime, text)
      previewTrack.addCue(cue)

      previewTrack.mode = 'showing'
      this.previewTrack = previewTrack
    }
  }

  /**
   * Hide and remove preview subtitle track
   */
  private hideSubtitlePreview(): void {
    if (!this.previewTrack) return

    this.previewTrack.mode = 'disabled'

    // Remove all cues
    const cues = this.previewTrack.cues
      ? Array.from(this.previewTrack.cues)
      : []
    cues.forEach((cue) => {
      if (this.previewTrack) {
        this.previewTrack.removeCue(cue)
      }
    })

    // Try to remove track from player
    try {
      const tracks = this.player_.textTracks()
      if (tracks && tracks.removeTrack) {
        tracks.removeTrack(this.previewTrack)
      }
    } catch (ignore) {
      // Track removal might not be supported in all browsers
    }

    this.previewTrack = null
  }
}

SettingsSubtitlesCustomMenu.options = {
  className: 'vjs-x-settings-custom-menu-container',
  children: [
    // Main menu with items
    SettingsSubtitlesCustomMenuMain.as('mainMenuEl'),

    // Sub-menus
    SettingsSubtitlesFontFamilyMenu.as('fontFamilyMenu'),
    SettingsSubtitlesFontColorMenu.as('fontColorMenu'),
    SettingsSubtitlesFontSizeMenu.as('fontSizeMenu'),
    SettingsSubtitlesFontOpacityMenu.as('fontOpacityMenu'),
    SettingsSubtitlesBackgroundColorMenu.as('backgroundColorMenu'),
    SettingsSubtitlesBackgroundOpacityMenu.as('backgroundOpacityMenu'),
    SettingsSubtitlesWindowColorMenu.as('windowColorMenu'),
    SettingsSubtitlesWindowOpacityMenu.as('windowOpacityMenu'),
    SettingsSubtitlesCharacterEdgeStyleMenu.as('characterEdgeStyleMenu'),
  ],
}
